import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";

import { useLoginMutation } from "../api/useLoginMutation";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { ROUTES } from "@/constants/routes";
import { ErrorResponse } from "@/types/response/ErrorResponse";
import { ValidationResponse } from "@/types/response/ValidationResponse";

interface LoginFormProps {
  passwordResetSuccess?: boolean;
}

const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm({ passwordResetSuccess }: LoginFormProps) {
  const { t } = useTranslation();
  const loginMutation = useLoginMutation();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate({
      email: data.email,
      password: data.password,
    });
  };

  // Get field errors from mutation error
  const getFieldError = (field: string) => {
    const response = loginMutation.failureReason?.response;
    if (!response) {
      return undefined;
    }
    const error = response.data;
    if (!error || !Array.isArray(error)) {
      return undefined;
    }
    const validationError = (error as ValidationResponse).find(
      (e) => e.propertyName === field,
    );
    if (!validationError) return undefined;
    return t(`errorCodes.${validationError.errorMessage}`);
  };

  // Get auth error (non-validation errors)
  const getAuthError = () => {
    const response = loginMutation.failureReason?.response;
    if (!response) {
      return undefined;
    }
    const error = response.data;
    if (!error || !("code" in error)) {
      return undefined;
    }
    return t(`errorCodes.${(error as ErrorResponse).code}`);
  };

  const authError = getAuthError();
  const emailError = getFieldError("email");
  const passwordError = getFieldError("password");

  // Sync server errors to form
  useEffect(() => {
    if (emailError) {
      form.setError("email", { message: emailError });
    }
    if (passwordError) {
      form.setError("password", { message: passwordError });
    }
  }, [emailError, passwordError, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {passwordResetSuccess && (
          <Alert className="mb-8">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle className="font-medium">
              {t("auth.pages.login.passwordResetSuccess")}
            </AlertTitle>
            <AlertDescription className="text-sm">
              {t("auth.pages.login.passwordResetMessage")}
            </AlertDescription>
          </Alert>
        )}

        {authError && (
          <Alert variant="destructive" className="mb-8">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle className="font-medium">{authError}</AlertTitle>
            <AlertDescription className="text-sm">
              {t("auth.pages.login.tryAgain")}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("common.email")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t("common.email")}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      loginMutation.reset();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("common.password")}
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("common.password")}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        loginMutation.reset();
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowPassword(!showPassword);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full"
          >
            {loginMutation.isPending
              ? t("common.loading", "Loading...")
              : t("common.signIn")}
          </Button>
          <Button variant="ghost" className="w-full" asChild>
            <Link to={ROUTES.forgotPassword}>
              {t("auth.pages.login.forgotPassword")}
            </Link>
          </Button>
        </div>
      </form>
    </Form>
  );
}
